import type { FC } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import { ProductCard } from '@packages/modules';
import type { ProductCardProps } from '@packages/modules/src/ProductCard/types';
import { setScrollingPosition, mergeQueryParameters } from '@packages/shared';
import { GlycerinPlacement, type GlycerinAdClickPayload } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { CriteoBeaconImage } from '../CriteoBeaconImage';
import { mapProductCardAvailabilities } from '../../utils/mapProductCardAvailabilities';
import { useProductCardPrice } from '../../hooks/useProductCardPrice';

export const CriteoProductCardFragment = graphql(/* GraphQL */ `
  fragment CriteoProductCardFragment on SponsoredStylePreviewItem {
    styleId # needed?
    styleName
    categoryName # tracking for criteo the same as regular cards?
    brand {
      name
      image
    }
    sellingPoints
    primaryVariationGroup {
      groupName
      sku
      image
      href
      price {
        ...SearchProductPriceFragment
      }
      availability {
        ...ProductAvailabilityOverlayFragment
      }
    }
    tracking {
      onLoad
      onView
    }
  }
`);

export type CriteoProductCardProps = {
  maskedData: FragmentType<typeof CriteoProductCardFragment>;
  showAdditionalInfo?: boolean;
};

const sponsoredFlagText = defineMessage({
  id: 'search.productList.sponsored.flag',
  defaultMessage: 'Gesponsert',
});

// TODO: make a common function in utils to handle card props for visible variation
// here and also in 'components/SearchProductCard/SearchProductCard.tsx:L162'
const useCriteoProductCardProps = (
  maskedData: FragmentType<typeof CriteoProductCardFragment>,
  query: ReturnType<typeof useRouter>['query'],
  showAdditionalInfo?: boolean,
): ProductCardProps => {
  const data = getFragmentData(CriteoProductCardFragment, maskedData);

  const variationGroup = data.primaryVariationGroup;

  // remove 'categories' and 'searchquery' from query object to avoid passing them to URL
  const { categories, searchquery, ...filteredQuery } = query;

  const productUrl = mergeQueryParameters(variationGroup.href, filteredQuery);

  const clickTrackingProps: GlycerinAdClickPayload = {
    adId: 'criteo',
    adType: 'criteo',
    category: 'criteo-card-click',
    target: productUrl,
    label: `${data.brand.name} ${data.styleName}`,
    placement: GlycerinPlacement.LISTING,
  };

  return {
    sku: variationGroup.sku,
    brand: data.brand.name,
    title: data.styleName,
    colors: [],
    availability: mapProductCardAvailabilities(variationGroup.availability),
    variationName: variationGroup.groupName ?? undefined,
    productLink: productUrl,
    image: variationGroup.image,
    additionalInfo: showAdditionalInfo ? data.sellingPoints : undefined,
    price: useProductCardPrice(variationGroup.price),
    clickTrackingProps,
  };
};

/**
 * ProductCard component used to render sponsored criteo products
 * */
export const CriteoProductCard: FC<CriteoProductCardProps> = ({
  maskedData,
  showAdditionalInfo,
}) => {
  const data = getFragmentData(CriteoProductCardFragment, maskedData);

  const { formatMessage } = useIntl();
  const router = useRouter();

  const productCardProps = useCriteoProductCardProps(maskedData, router.query, showAdditionalInfo);

  return (
    <>
      {data.tracking.onLoad && <CriteoBeaconImage src={data.tracking.onLoad} name="onLoad" />}
      {data.tracking.onView && <CriteoBeaconImage src={data.tracking.onView} name="onView" />}
      <ProductCard
        {...productCardProps}
        onProductLinkActionAreaClick={setScrollingPosition}
        flags={[{ text: formatMessage(sponsoredFlagText), type: 'neutral' }]}
        isSponsored
        priority
        shrinkToFit
      />
    </>
  );
};
